import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Building2, Users, Upload, BookOpen, BarChart3, Plus, TrendingUp, Target, ArrowRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrgDashboard() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [showAssign, setShowAssign] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [dashRes, progsRes] = await Promise.all([
        axios.get(`${API}/org/dashboard`, { withCredentials: true }),
        axios.get(`${API}/programs`)
      ]);
      setDashboard(dashRes.data);
      setPrograms(progsRes.data);
      if (dashRes.data.org) {
        const perfRes = await axios.get(`${API}/org/performance`, { withCredentials: true });
        setPerformance(perfRes.data.students || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createOrg = async () => {
    if (!orgName.trim()) return;
    try {
      await axios.post(`${API}/org/create`, { name: orgName }, { withCredentials: true });
      toast.success('Organization created!');
      setShowOrgForm(false);
      fetchAll();
    } catch { toast.error('Failed to create organization'); }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/org/students/upload`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Added ${res.data.added} students`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  };

  const addStudent = async () => {
    if (!addEmail.trim()) return;
    try {
      await axios.post(`${API}/org/students/add`, { email: addEmail, name: addName }, { withCredentials: true });
      toast.success('Student added');
      setAddEmail(''); setAddName('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const assignProgram = async (programId) => {
    try {
      await axios.post(`${API}/org/assign-program`, { program_id: programId }, { withCredentials: true });
      toast.success('Program assigned');
      setShowAssign(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="org-loading"><div className="mono-data text-lg animate-pulse-red">Loading...</div></div>;
  }

  const org = dashboard?.org;
  const stats = dashboard?.stats;

  // No org yet
  if (!org) {
    return (
      <div className="min-h-screen" data-testid="org-setup">
        <div className="border-b border-[#09090B] py-16">
          <div className="container-swiss text-center max-w-lg mx-auto">
            <Building2 className="w-12 h-12 text-[#09090B] mx-auto mb-6" />
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              B2B College Portal
            </h1>
            <p className="text-zinc-600 mb-8">Create an organization to manage students, assign programs, and track cognitive training performance at scale.</p>
            {!showOrgForm ? (
              <button onClick={() => setShowOrgForm(true)} className="btn-accent" data-testid="create-org-btn">Create Organization</button>
            ) : (
              <div className="border border-[#09090B] p-6 text-left">
                <input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Organization name (e.g., Stanford University)" className="input-swiss text-base mb-4" data-testid="org-name-input" />
                <div className="flex gap-3">
                  <button onClick={createOrg} disabled={!orgName.trim()} className="btn-primary text-xs px-6 py-2 disabled:opacity-40" data-testid="submit-org-btn">Create</button>
                  <button onClick={() => setShowOrgForm(false)} className="btn-secondary text-xs px-6 py-2">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const chartData = performance.filter(s => s.sessions_completed > 0).map(s => ({
    name: s.name?.split(' ')[0] || s.email?.split('@')[0],
    score: s.avg_score,
    accuracy: s.avg_accuracy,
    sessions: s.sessions_completed
  }));

  return (
    <div className="min-h-screen" data-testid="org-dashboard">
      {/* Header */}
      <div className="border-b border-[#09090B] py-8">
        <div className="container-swiss flex items-center justify-between">
          <div>
            <p className="mono-data text-[#09090B] mb-1">Organization</p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="org-name">{org.name}</h1>
          </div>
          <div className="mono-data text-xs text-zinc-500">{org.plan?.toUpperCase()} PLAN</div>
        </div>
      </div>

      {/* Stats */}
      <div className="border-b border-[#09090B]">
        <div className="container-swiss">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-0 border-l border-t border-[#09090B]">
            {[
              { label: 'Students', value: stats.total_students, icon: Users },
              { label: 'Active', value: stats.active_students, icon: TrendingUp },
              { label: 'Attempts', value: stats.total_attempts, icon: BarChart3 },
              { label: 'Avg Score', value: `${stats.avg_score}%`, icon: Target },
              { label: 'Accuracy', value: `${stats.avg_accuracy}%`, icon: Target },
              { label: 'Engagement', value: `${stats.engagement_rate}%`, icon: TrendingUp },
            ].map(s => (
              <div key={s.label} className="grid-border-item" data-testid={`org-stat-${s.label.toLowerCase()}`}>
                <div className="label-swiss mb-2 flex items-center gap-1"><s.icon className="w-3 h-3" /> {s.label}</div>
                <div className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container-swiss py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Student Management */}
          <div className="lg:col-span-1">
            <h3 className="mono-data text-sm mb-4 flex items-center gap-2"><Users className="w-3 h-3" /> Manage Students</h3>

            {/* CSV Upload */}
            <div className="border border-[#09090B] p-4 mb-4">
              <label className="flex items-center gap-2 cursor-pointer btn-secondary text-xs px-4 py-2 w-full justify-center" data-testid="csv-upload-btn">
                <Upload className="w-3 h-3" /> Upload CSV
                <input type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" />
              </label>
              <p className="text-xs text-zinc-400 mt-2 text-center">CSV format: name, email</p>
            </div>

            {/* Add Single Student */}
            <div className="border border-[#09090B] p-4 mb-4">
              <h4 className="mono-data text-xs mb-2">Add Student</h4>
              <input value={addName} onChange={e => setAddName(e.target.value)} placeholder="Name" className="input-swiss text-sm mb-2" data-testid="add-student-name" />
              <input value={addEmail} onChange={e => setAddEmail(e.target.value)} placeholder="Email" className="input-swiss text-sm mb-2" data-testid="add-student-email" />
              <button onClick={addStudent} disabled={!addEmail.trim()} className="btn-primary text-xs px-4 py-2 w-full disabled:opacity-40" data-testid="add-student-btn">Add</button>
            </div>

            {/* Assign Program */}
            <div className="border border-[#09090B] p-4 mb-4">
              <h4 className="mono-data text-xs mb-2 flex items-center gap-2"><BookOpen className="w-3 h-3" /> Assigned Programs</h4>
              {org.assigned_programs?.length > 0 && (
                <div className="space-y-1 mb-3">
                  {org.assigned_programs.map(pid => {
                    const prog = programs.find(p => p.program_id === pid);
                    return <div key={pid} className="text-xs text-zinc-600 py-1 border-b border-zinc-100">{prog?.title || pid}</div>;
                  })}
                </div>
              )}
              {!showAssign ? (
                <button onClick={() => setShowAssign(true)} className="btn-secondary text-xs px-4 py-2 w-full flex items-center justify-center gap-2" data-testid="assign-program-toggle">
                  <Plus className="w-3 h-3" /> Assign Program
                </button>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {programs.filter(p => p.is_free && !org.assigned_programs?.includes(p.program_id)).map(p => (
                    <button key={p.program_id} onClick={() => assignProgram(p.program_id)} className="w-full text-left text-xs p-2 hover:bg-zinc-50 border-b border-zinc-100 flex items-center justify-between" data-testid={`assign-${p.program_id}`}>
                      <span>{p.title}</span><ArrowRight className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Performance */}
          <div className="lg:col-span-2">
            <h3 className="mono-data text-sm mb-4 flex items-center gap-2"><BarChart3 className="w-3 h-3" /> Student Performance</h3>

            {/* Chart */}
            {chartData.length > 0 && (
              <div className="border border-[#09090B] p-4 mb-4" data-testid="performance-chart">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="score" fill="#09090B" name="Score" />
                    <Bar dataKey="accuracy" fill="#09090B" name="Accuracy" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Student Table */}
            <div className="border border-[#09090B]" data-testid="student-table">
              <div className="grid grid-cols-5 gap-0 border-b border-[#09090B] bg-zinc-50">
                {['Name', 'Email', 'Status', 'Sessions', 'Avg Score'].map(h => (
                  <div key={h} className="p-3 mono-data text-xs">{h}</div>
                ))}
              </div>
              {performance.length === 0 ? (
                <div className="p-8 text-center text-zinc-400 text-sm">No students added yet. Upload a CSV or add manually.</div>
              ) : (
                performance.map(s => (
                  <div key={s.member_id} className="grid grid-cols-5 gap-0 border-b border-zinc-200 last:border-0" data-testid={`student-row-${s.member_id}`}>
                    <div className="p-3 text-sm font-medium truncate">{s.name}</div>
                    <div className="p-3 text-xs text-zinc-500 truncate">{s.email}</div>
                    <div className="p-3">
                      <span className={`mono-data text-xs ${s.status === 'active' ? 'text-[#09090B]' : 'text-zinc-400'}`}>{s.status}</span>
                    </div>
                    <div className="p-3 mono-data text-sm">{s.sessions_completed}</div>
                    <div className="p-3 mono-data text-sm">{s.avg_score}%</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
